import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Layers, Loader2, Sparkles, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { AvatarPortrait } from "@/components/AvatarPortrait";
import { AvatarCardSkeleton, Skeleton } from "@/components/Skeleton";
import { FreeQuotaChip } from "@/components/FreeQuotaChip";
import { PaywallDialog } from "@/components/PaywallDialog";

const DIFFICULTY_TONE = {
    beginner: "bg-emerald-50 text-emerald-700 border-emerald-200",
    intermediate: "bg-amber-50 text-amber-800 border-amber-200",
    advanced: "bg-rose-50 text-rose-700 border-rose-200",
};

export default function PracticeSetupPage() {
    const { user, isAdmin, isSubscribed } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const continueFromId = searchParams.get("continue_from");

    // step state — held in memory only (refresh resets per spec)
    const [step, setStep] = useState(1);
    const [filter, setFilter] = useState("individuals"); // "individuals" | "couples"
    const [selectedAvatar, setSelectedAvatar] = useState(null);

    const [therapyTypeSlug, setTherapyTypeSlug] = useState(null);
    const [caseSlug, setCaseSlug] = useState(null);
    const [duration, setDuration] = useState(30);
    const [counsellorName, setCounsellorName] = useState(user?.full_name || "");

    const [avatars, setAvatars] = useState(null); // null = loading
    const [therapyTypes, setTherapyTypes] = useState(null);
    const [cases, setCases] = useState(null);
    const [generatingCase, setGeneratingCase] = useState(false);
    const [generateError, setGenerateError] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    // Phase 15 — paywall + quota chip.
    const [paymentsConfig, setPaymentsConfig] = useState(null);
    const [paywallOpen, setPaywallOpen] = useState(false);
    const [paywallContext, setPaywallContext] = useState({ free_used: 2, free_quota: 2 });

    useEffect(() => {
        let cancelled = false;
        api.get("/payments/config")
            .then((r) => { if (!cancelled) setPaymentsConfig(r.data); })
            .catch(() => { /* non-fatal — chip just doesn't render */ });
        return () => { cancelled = true; };
    }, []);

    // Phase 8 — continuation context. When `continue_from` is in the URL we
    // pre-fill avatar/therapy/case from the source session, jump to Step 2,
    // and route the submit through POST /sessions/{id}/continue.
    const [continueContext, setContinueContext] = useState(null);
    // { sourceSession, nextNumber }
    const [continueLoading, setContinueLoading] = useState(Boolean(continueFromId));

    // load avatars + therapy types once on mount
    useEffect(() => {
        let cancelled = false;
        Promise.all([
            api.get("/avatars"),
            api.get("/therapy-types"),
        ])
            .then(([a, t]) => {
                if (cancelled) return;
                setAvatars(a.data);
                setTherapyTypes(t.data);
            })
            .catch(() => {
                if (cancelled) return;
                toast.error("Could not load practice options. Please retry.");
                setAvatars([]);
                setTherapyTypes([]);
            });
        return () => { cancelled = true; };
    }, []);

    // Phase 8 — load source session and pre-fill when ?continue_from is present.
    useEffect(() => {
        if (!continueFromId || !avatars || !therapyTypes) return;
        let cancelled = false;
        api.get(`/sessions/${continueFromId}`)
            .then((res) => {
                if (cancelled) return;
                const src = res.data;
                if (!["completed", "reviewed"].includes(src.status)) {
                    toast.error("This session can't be continued yet.");
                    setContinueLoading(false);
                    return;
                }
                if (src.session_number_in_series >= 8) {
                    toast.error("This series is already at session 8 of 8.");
                    setContinueLoading(false);
                    return;
                }
                const av = avatars.find((a) => a.slug === src.avatar_slug);
                if (!av) {
                    toast.error("Avatar from previous session not found.");
                    setContinueLoading(false);
                    return;
                }
                setSelectedAvatar(av);
                setFilter(av.is_couple ? "couples" : "individuals");
                setTherapyTypeSlug(src.therapy_type_slug);
                setCaseSlug(src.case_slug);
                setDuration(src.duration_minutes);
                setCounsellorName(src.counsellor_display_name || user?.full_name || "");
                setContinueContext({
                    sourceSession: src,
                    nextNumber: src.session_number_in_series + 1,
                });
                setStep(2);
                setContinueLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                toast.error("Could not load the session to continue.");
                setContinueLoading(false);
            });
        return () => { cancelled = true; };
    }, [continueFromId, avatars, therapyTypes, user]);

    // when avatar changes (or therapy filter changes), refetch cases. If the
    // (avatar, therapy) combo has zero static cases, ask the backend to
    // generate one on-the-fly (POST /cases/generate). The generated case is
    // persisted server-side so a refresh hits the cached row instead of
    // burning another LLM call.
    useEffect(() => {
        if (!selectedAvatar || !therapyTypeSlug) {
            setCases(null);
            setGeneratingCase(false);
            setGenerateError(false);
            return;
        }
        let cancelled = false;
        setCases(null);
        setGeneratingCase(false);
        setGenerateError(false);
        api.get("/cases", {
            params: {
                therapy_type: therapyTypeSlug,
                avatar_slug: selectedAvatar.slug,
            },
        })
            .then(async (res) => {
                if (cancelled) return;
                if (res.data && res.data.length > 0) {
                    setCases(res.data);
                    if (caseSlug && !res.data.some((c) => c.slug === caseSlug)) {
                        setCaseSlug(null);
                    }
                    return;
                }
                // Empty combo → generate one inline.
                setGeneratingCase(true);
                try {
                    const gen = await api.post("/cases/generate", {
                        avatar_slug: selectedAvatar.slug,
                        therapy_type_slug: therapyTypeSlug,
                    });
                    if (cancelled) return;
                    setCases([gen.data]);
                    setCaseSlug(gen.data.slug);
                } catch (err) {
                    if (cancelled) return;
                    // 429 has a friendly detail; surface it as a toast and
                    // fall back to the error state. 502 / network just goes
                    // to the retry empty state.
                    const status = err?.response?.status;
                    const detail = err?.response?.data?.detail;
                    if (status === 429 && typeof detail === "string") {
                        toast.error(detail);
                    }
                    setGenerateError(true);
                    setCases([]);
                } finally {
                    if (!cancelled) setGeneratingCase(false);
                }
            })
            .catch(() => {
                if (cancelled) return;
                setCases([]);
                setGenerateError(true);
            });
        return () => { cancelled = true; };
    }, [selectedAvatar, therapyTypeSlug]); // eslint-disable-line react-hooks/exhaustive-deps

    // pick the right therapy type when avatar is selected
    useEffect(() => {
        if (!selectedAvatar || !therapyTypes) return;
        if (selectedAvatar.is_couple) {
            setTherapyTypeSlug("couples");
        } else {
            // if previously-picked therapy was "couples", reset
            if (therapyTypeSlug === "couples" || !therapyTypeSlug) {
                setTherapyTypeSlug(null);
            }
        }
    }, [selectedAvatar, therapyTypes]); // eslint-disable-line react-hooks/exhaustive-deps

    const filteredAvatars = useMemo(() => {
        if (!avatars) return null;
        const wantCouple = filter === "couples";
        return avatars.filter((a) => a.is_couple === wantCouple);
    }, [avatars, filter]);

    const availableTherapyTypes = useMemo(() => {
        if (!therapyTypes || !selectedAvatar) return [];
        return selectedAvatar.is_couple
            ? therapyTypes.filter((t) => t.slug === "couples")
            : therapyTypes.filter((t) => t.slug !== "couples");
    }, [therapyTypes, selectedAvatar]);

    const canSubmit =
        !!selectedAvatar &&
        !!therapyTypeSlug &&
        !!caseSlug &&
        [30, 45, 60].includes(duration) &&
        counsellorName.trim().length > 0;

    const handleStart = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            if (continueContext) {
                const res = await api.post(
                    `/sessions/${continueContext.sourceSession.id}/continue`,
                    {
                        duration_minutes: duration,
                        counsellor_display_name: counsellorName.trim(),
                    }
                );
                toast.success(`Session ${res.data.session_number_in_series} of 8 — let's go.`);
                navigate(`/practice/session/${res.data.id}`);
                return;
            }
            const res = await api.post("/sessions", {
                avatar_slug: selectedAvatar.slug,
                therapy_type_slug: therapyTypeSlug,
                case_slug: caseSlug,
                duration_minutes: duration,
                counsellor_display_name: counsellorName.trim(),
            });
            toast.success("Session ready. Good luck.");
            navigate(`/practice/session/${res.data.id}`);
        } catch (err) {
            // Phase 15 — 402 means free quota exhausted. Show the paywall, don't toast.
            if (err?.response?.status === 402) {
                const detail = err.response.data?.detail || {};
                setPaywallContext({
                    free_used: detail.free_used ?? 2,
                    free_quota: detail.free_quota ?? 2,
                });
                setPaywallOpen(true);
            } else {
                const detail = err?.response?.data?.detail;
                toast.error(typeof detail === "string" ? detail : "Could not start session");
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Layout>
            <div className="animate-fade-in-up space-y-8" data-testid="practice-setup-page">
                {/* page heading + step pill */}
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="text-sm font-medium text-primary">
                            {continueContext ? "Continuing your series" : `Step ${step} of 2`}
                        </p>
                        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                            {continueContext
                                ? `Session ${continueContext.nextNumber} of 8`
                                : step === 1
                                    ? "Choose a client"
                                    : "Session details"}
                        </h1>
                        <p className="mt-2 text-sm text-muted-foreground">
                            {continueContext
                                ? `Carrying forward with ${continueContext.sourceSession.avatar_name}. Confirm the details and pick up where you left off.`
                                : step === 1
                                    ? "Pick someone whose presenting issue feels useful to practise with."
                                    : "Pick the therapy approach, a case to work, and how long the session should run."}
                        </p>
                        <FreeQuotaChip
                            subscription={paymentsConfig?.subscription}
                            isAdmin={isAdmin}
                            isSubscribed={isSubscribed}
                            className="mt-3"
                            dataTestId="practice-setup-free-chip"
                        />
                    </div>
                </div>

                {continueLoading && (
                    <Card className="border-dashed border-border" data-testid="setup-continue-loading">
                        <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading your previous session…
                        </CardContent>
                    </Card>
                )}

                {!continueLoading && step === 1 && (
                    <Step1Avatars
                        filter={filter}
                        setFilter={setFilter}
                        avatars={filteredAvatars}
                        selectedAvatar={selectedAvatar}
                        onPick={setSelectedAvatar}
                        onContinue={() => setStep(2)}
                    />
                )}

                {!continueLoading && step === 2 && selectedAvatar && (
                    <Step2Details
                        avatar={selectedAvatar}
                        therapyTypes={availableTherapyTypes}
                        therapyTypeSlug={therapyTypeSlug}
                        setTherapyTypeSlug={setTherapyTypeSlug}
                        cases={cases}
                        caseSlug={caseSlug}
                        setCaseSlug={setCaseSlug}
                        generatingCase={generatingCase}
                        generateError={generateError}
                        onRetryGenerate={() => {
                            // bump therapy state through a no-op to retrigger
                            // the case-fetch effect.
                            setGenerateError(false);
                            const t = therapyTypeSlug;
                            setTherapyTypeSlug(null);
                            setTimeout(() => setTherapyTypeSlug(t), 0);
                        }}
                        duration={duration}
                        setDuration={setDuration}
                        counsellorName={counsellorName}
                        setCounsellorName={setCounsellorName}
                        onBack={() => setStep(1)}
                        onSubmit={handleStart}
                        submitting={submitting}
                        canSubmit={canSubmit}
                        continueContext={continueContext}
                    />
                )}
            </div>
            <PaywallDialog
                open={paywallOpen}
                onOpenChange={setPaywallOpen}
                freeUsed={paywallContext.free_used}
                freeQuota={paywallContext.free_quota}
                priceInr={paymentsConfig?.price_inr || 2999}
            />
        </Layout>
    );
}

/* =====================================================================
 * Step 1 — pick an avatar
 * ===================================================================== */
const Step1Avatars = ({ filter, setFilter, avatars, selectedAvatar, onPick, onContinue }) => {
    return (
        <>
            <div className="sticky top-16 z-10 -mx-4 bg-background/85 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
                <Tabs value={filter} onValueChange={setFilter}>
                    <TabsList data-testid="avatar-filter-tabs">
                        <TabsTrigger value="individuals" data-testid="tab-individuals">
                            Individuals
                        </TabsTrigger>
                        <TabsTrigger value="couples" data-testid="tab-couples">
                            Couples
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {avatars === null ? (
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                    {Array.from({ length: 9 }).map((_, i) => (
                        <AvatarCardSkeleton key={i} />
                    ))}
                </div>
            ) : avatars.length === 0 ? (
                <Card className="border-dashed">
                    <CardContent className="p-10 text-center text-sm text-muted-foreground">
                        No avatars in this category yet.
                    </CardContent>
                </Card>
            ) : (
                <div
                    className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3"
                    data-testid="avatar-grid"
                >
                    {avatars.map((avatar) => (
                        <AvatarCard
                            key={avatar.slug}
                            avatar={avatar}
                            selected={selectedAvatar?.slug === avatar.slug}
                            onPick={() => onPick(avatar)}
                        />
                    ))}
                </div>
            )}

            {selectedAvatar && (
                <div
                    className="sticky bottom-0 -mx-4 flex items-center justify-between gap-4 border-t border-border bg-background/95 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6"
                    data-testid="step1-sticky-footer"
                >
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="h-10 w-10 shrink-0 overflow-hidden rounded-lg">
                            <AvatarPortrait
                                name={selectedAvatar.name}
                                imageUrl={selectedAvatar.image_url}
                                aspect={selectedAvatar.is_couple ? "wide" : "square"}
                                rounded="rounded-lg"
                                dataTestId="step1-selected-avatar-thumb"
                            />
                        </div>
                        <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-foreground">
                                {selectedAvatar.name}
                            </p>
                            <p className="truncate text-xs text-muted-foreground">
                                {selectedAvatar.presenting_issue_short}
                            </p>
                        </div>
                    </div>
                    <Button
                        size="lg"
                        className="bg-primary hover:bg-primary-hover"
                        onClick={onContinue}
                        data-testid="step1-continue-button"
                    >
                        Continue
                        <ArrowRight className="h-4 w-4" />
                    </Button>
                </div>
            )}
        </>
    );
};

const AvatarCard = ({ avatar, selected, onPick }) => {
    const aspect = avatar.is_couple ? "wide" : "square";
    return (
        <button
            type="button"
            onClick={onPick}
            className={cn(
                "group relative flex flex-col gap-3 rounded-2xl border border-border bg-surface p-3 text-left transition-all",
                "hover:-translate-y-0.5 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                selected && "ring-2 ring-primary border-primary/60 shadow-md"
            )}
            data-testid={`avatar-card-${avatar.slug}`}
        >
            <div className="overflow-hidden rounded-xl">
                <AvatarPortrait
                    name={avatar.name}
                    imageUrl={avatar.image_url}
                    aspect={aspect}
                    rounded="rounded-xl"
                    dataTestId={`avatar-portrait-${avatar.slug}`}
                />
            </div>
            <div className="space-y-1">
                <p className="font-medium text-foreground">
                    {avatar.name}
                    <span className="ml-2 text-sm font-normal text-muted-foreground">
                        {avatar.is_couple ? `Couple` : `${avatar.age}${avatar.sex}`}
                    </span>
                </p>
                <p className="line-clamp-1 text-xs text-muted-foreground">
                    {avatar.presenting_issue_short}
                </p>
            </div>
            {selected && (
                <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground shadow">
                    <Sparkles className="h-3 w-3" />
                    Selected
                </span>
            )}
        </button>
    );
};

/* =====================================================================
 * Step 2 — therapy + case + duration
 * ===================================================================== */
const Step2Details = ({
    avatar, therapyTypes, therapyTypeSlug, setTherapyTypeSlug,
    cases, caseSlug, setCaseSlug,
    generatingCase, generateError, onRetryGenerate,
    duration, setDuration,
    counsellorName, setCounsellorName,
    onBack, onSubmit, submitting, canSubmit,
    continueContext,
}) => {
    return (
        <>
            {continueContext && (
                <Card
                    className="border-primary/30 bg-primary/[0.04] shadow-sm"
                    data-testid="step2-continue-banner"
                >
                    <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:gap-4">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
                            <Layers className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                            <p className="font-medium text-foreground" data-testid="step2-continue-banner-title">
                                Continuing your series with {avatar.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                This will be session {continueContext.nextNumber} of 8. The case and avatar are locked — adjust duration or your display name and start when you&apos;re ready.
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Picked-avatar chip */}
            <Card className="border-border/80 shadow-sm" data-testid="step2-avatar-chip">
                <CardContent className="flex items-center justify-between gap-4 p-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg">
                            <AvatarPortrait
                                name={avatar.name}
                                imageUrl={avatar.image_url}
                                aspect={avatar.is_couple ? "wide" : "square"}
                                rounded="rounded-lg"
                                dataTestId="step2-avatar-thumb"
                            />
                        </div>
                        <div className="min-w-0">
                            <p className="truncate font-medium text-foreground">{avatar.name}</p>
                            <p className="truncate text-xs text-muted-foreground">
                                {avatar.presenting_issue_short}
                            </p>
                        </div>
                    </div>
                    {!continueContext && (
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={onBack}
                            data-testid="step2-change-avatar-button"
                        >
                            <X className="h-4 w-4" />
                            Change
                        </Button>
                    )}
                </CardContent>
            </Card>

            <div className="space-y-8">
                {/* counsellor display name */}
                <section className="space-y-3">
                    <Label htmlFor="counsellor_display_name">Your display name in session</Label>
                    <Input
                        id="counsellor_display_name"
                        value={counsellorName}
                        onChange={(e) => setCounsellorName(e.target.value)}
                        className="max-w-md"
                        data-testid="counsellor-name-input"
                    />
                </section>

                {/* therapy type pills */}
                <section className="space-y-3">
                    <Label>Therapy approach</Label>
                    {avatar.is_couple && (
                        <p className="text-xs text-muted-foreground">
                            Couple clients use the Couples (Gottman-informed) approach.
                        </p>
                    )}
                    <div className="flex flex-wrap gap-2" data-testid="therapy-type-pills">
                        {therapyTypes.map((t) => {
                            const active = therapyTypeSlug === t.slug;
                            return (
                                <button
                                    key={t.slug}
                                    type="button"
                                    onClick={() => setTherapyTypeSlug(t.slug)}
                                    className={cn(
                                        "rounded-full border px-4 py-1.5 text-sm transition-colors",
                                        active
                                            ? "border-primary bg-primary text-primary-foreground"
                                            : "border-border bg-surface text-foreground hover:bg-secondary"
                                    )}
                                    data-testid={`therapy-pill-${t.slug}`}
                                >
                                    {t.name}
                                </button>
                            );
                        })}
                    </div>
                </section>

                {/* case selection */}
                <section className="space-y-3">
                    <Label>Case</Label>
                    {!therapyTypeSlug ? (
                        <p className="text-sm text-muted-foreground">
                            Pick a therapy approach above to see compatible cases.
                        </p>
                    ) : cases === null && !generatingCase ? (
                        <div className="grid gap-3 sm:grid-cols-2">
                            <Skeleton className="h-24 rounded-xl" />
                            <Skeleton className="h-24 rounded-xl" />
                        </div>
                    ) : generatingCase ? (
                        <Card className="border-dashed" data-testid="case-generating">
                            <CardContent className="flex items-center gap-3 p-6 text-sm text-muted-foreground">
                                <Loader2 className="h-4 w-4 animate-spin text-primary" />
                                <span>Generating a case for you — usually 5–10 seconds…</span>
                            </CardContent>
                        </Card>
                    ) : generateError && (!cases || cases.length === 0) ? (
                        <Card className="border-dashed" data-testid="case-generate-error">
                            <CardContent className="flex flex-col items-start gap-3 p-6 text-sm text-muted-foreground">
                                <span>We couldn&apos;t generate a case for this combination just now.</span>
                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={onRetryGenerate}
                                    data-testid="case-generate-retry"
                                >
                                    Try again
                                </Button>
                            </CardContent>
                        </Card>
                    ) : cases && cases.length === 0 ? (
                        <Card className="border-dashed">
                            <CardContent className="p-6 text-sm text-muted-foreground">
                                No cases for this combination yet. Try a different therapy approach.
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid gap-3 sm:grid-cols-2" data-testid="case-list">
                            {cases.map((c) => (
                                <CaseRadio
                                    key={c.slug}
                                    caseItem={c}
                                    selected={caseSlug === c.slug}
                                    onSelect={() => setCaseSlug(c.slug)}
                                />
                            ))}
                        </div>
                    )}
                </section>

                {/* duration */}
                <section className="space-y-3">
                    <Label>Duration</Label>
                    <div className="flex gap-2" data-testid="duration-pills">
                        {[30, 45, 60].map((d) => {
                            const active = duration === d;
                            return (
                                <button
                                    key={d}
                                    type="button"
                                    onClick={() => setDuration(d)}
                                    className={cn(
                                        "rounded-full border px-4 py-1.5 text-sm transition-colors",
                                        active
                                            ? "border-primary bg-primary text-primary-foreground"
                                            : "border-border bg-surface text-foreground hover:bg-secondary"
                                    )}
                                    data-testid={`duration-pill-${d}`}
                                >
                                    {d} min
                                </button>
                            );
                        })}
                    </div>
                </section>
            </div>

            {/* sticky submit */}
            <div
                className="sticky bottom-0 -mx-4 flex items-center justify-between gap-4 border-t border-border bg-background/95 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6"
                data-testid="step2-sticky-footer"
            >
                <Button
                    variant="ghost"
                    onClick={onBack}
                    data-testid="step2-back-button"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                </Button>
                <Button
                    size="lg"
                    onClick={onSubmit}
                    disabled={!canSubmit || submitting}
                    className="bg-primary hover:bg-primary-hover"
                    data-testid="step2-start-session-button"
                >
                    {submitting ? (
                        <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Starting…
                        </>
                    ) : (
                        <>
                            Start session
                            <ArrowRight className="h-4 w-4" />
                        </>
                    )}
                </Button>
            </div>
        </>
    );
};

const CaseRadio = ({ caseItem, selected, onSelect }) => (
    <button
        type="button"
        onClick={onSelect}
        className={cn(
            "flex flex-col items-start gap-2 rounded-xl border border-border bg-surface p-4 text-left transition-all",
            "hover:-translate-y-0.5 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            selected && "border-primary/60 ring-2 ring-primary shadow-sm"
        )}
        data-testid={`case-card-${caseItem.slug}`}
    >
        <div className="flex w-full items-center justify-between gap-2">
            <span className="font-medium text-foreground">{caseItem.title}</span>
            <div className="flex items-center gap-2">
                {caseItem.is_dynamic && (
                    <Badge
                        variant="outline"
                        className="border-transparent bg-accent text-accent-foreground text-xs"
                        data-testid={`case-suggested-badge-${caseItem.slug}`}
                    >
                        Suggested
                    </Badge>
                )}
                <Badge
                    variant="outline"
                    className={cn("text-xs capitalize border", DIFFICULTY_TONE[caseItem.difficulty])}
                >
                    {caseItem.difficulty}
                </Badge>
            </div>
        </div>
        <p className="line-clamp-2 text-xs text-muted-foreground">{caseItem.description}</p>
    </button>
);
